import type {
    RequiredTranslatable,
    Translatable,
} from "@@/shared/translations/translation";
import { translation } from "@@/shared/translations/translation";
import { assertTypeMatch } from "@@/utilities/type-helpers";
import type { IsEqual } from "type-fest";
import { z } from "zod";
import type { Resource } from "./resource";
import type { ResourceGroupType } from "./resource-group";

type ResourceType = Resource["_type"];
const resourceTypes = [
    "RESOURCE_GROUP",
    "RESOURCE_TABLE",
    "RESOURCE_SHARED",
    "RESOURCE_GENERIC",
    "RESOURCE_LOCAL",
    "RESOURCE_RESERVED",
    "RESOURCE_ACCOMMODATION",
    "RESOURCE_TABLE_GROUP",
] as const;
const resourceTypeZodEnum: z.ZodSchema<ResourceType> = z.enum(resourceTypes);

const nonGroupResourceTypes = [
    "RESOURCE_TABLE",
    "RESOURCE_SHARED",
    "RESOURCE_GENERIC",
    "RESOURCE_LOCAL",
    "RESOURCE_RESERVED",
    "RESOURCE_ACCOMMODATION",
    "RESOURCE_TABLE_GROUP",
] as const;
type NonGroupResourceType = (typeof nonGroupResourceTypes)[number];
const nonGroupResourceTypeZodEnum = z.enum(nonGroupResourceTypes);

// Make sure the types are always in sync
// To prevent bugs where a new type is added to one but not the other
assertTypeMatch<
    IsEqual<NonGroupResourceType, Exclude<ResourceType, ResourceGroupType>>
>();

const resourceTypeMeta: Record<
    ResourceType,
    {
        title: RequiredTranslatable;
        description?: Translatable;
        iconName: string;
    }
> = {
    RESOURCE_GENERIC: {
        title: translation({
            sv: "Generell",
            en: "Generic",
        }),
        iconName: "fa-box",
    },
    RESOURCE_GROUP: {
        title: translation({
            sv: "Resursgrupp",
            en: "Resource group",
        }),
        iconName: "fa-boxes-stacked",
    },
    RESOURCE_LOCAL: {
        title: translation({
            sv: "Lokal resurs",
            en: "Local resource",
        }),
        description: translation({
            sv: "Antalet tillgängliga resurser delas inte mellan pass utan varje pass har sitt eget antal tillgängliga resurser",
            en: "The number of available resources is not shared between sessions, but each session has its own number of available resources",
        }),
        iconName: "fa-box",
    },
    RESOURCE_RESERVED: {
        title: translation({
            sv: "Reserverbar",
            en: "Reservable",
        }),
        description: translation({
            sv: "Resursen tillgänglig för flera pass men reserveras för ett specifikt pass när en första bokningen görs. Därefter kan resursen inte bokas in på ett annat pass under tiden passet med första reserveringen av resursen pågår. Fler bokningar kan dock göras på passet som redan har resursen tillgänglig.",
            en: "The resource is available for multiple sessions but is reserved for a specific session when the first booking is made. After that, the resource cannot be booked for another session while the session with the first reservation of the resource is ongoing. However, more bookings can be made for the session that already has the resource available.",
        }),
        iconName: "fa-box",
    },
    RESOURCE_SHARED: {
        title: translation({
            sv: "Delad resurs",
            en: "Shared resource",
        }),
        description: translation({
            sv: "Resursantal delas mellan pass. Om en resurs bokas på ett pass minskar antalet tillgängliga resurser för alla pass. Om en resurs bokas på ett pass och sedan avbokas ökar antalet tillgängliga resurser för alla pass.",
            en: "The number of resources is shared between sessions. If a resource is booked for a session, the number of available resources is decreased for all sessions. If a resource is booked for a session and then canceled, the number of available resources is increased for all sessions.",
        }),
        iconName: "fa-box",
    },
    RESOURCE_TABLE: {
        title: translation({
            sv: "Bord",
            en: "Table",
        }),
        description: translation({
            sv: "Ett bord med ett angivet antal platser. Används exempelvis vid bordsbokning.",
            en: "A table with a specified number of seats. Used, for example, for table reservations.",
        }),
        iconName: "fa-utensils",
    },
    RESOURCE_ACCOMMODATION: {
        title: translation({
            sv: "Boende",
            en: "Accommodation",
        }),
        description: translation({
            sv: "Rum, stuga, ställplats, tältplats, jurta etc.",
            en: "Room, cabin, camping spot, tent spot, yurt, etc.",
        }),
        iconName: "fa-house",
    },
    RESOURCE_TABLE_GROUP: {
        title: translation({
            sv: "Bordskombination",
            en: "Table combination",
        }),
        description: translation({
            sv: "En grupp av bord som bokas tillsammans",
            en: "A group of tables that can be booked together",
        }),
        iconName: "fa-utensils",
    },
};

const isResourceType = (type: string): type is ResourceType =>
    resourceTypes.includes(type as ResourceType);

export {
    isResourceType,
    nonGroupResourceTypes,
    nonGroupResourceTypeZodEnum,
    resourceTypeMeta,
    resourceTypes,
    resourceTypeZodEnum,
};
export type { NonGroupResourceType, ResourceType };
